import { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/Search";
import RedeemIcon from "@mui/icons-material/Redeem";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getCurrencySymbol, formatCurrencyAmount } from "../../Utility/currencyDisplay";

// Fallbacks only — real values come from api/casino/manualdepositconfig.
export const DEFAULT_BONUS_MULTIPLIER = 1.5;
const DEFAULT_CHIPS = [10, 20, 30];
const MIN_DEPOSIT = 10;

const GREEN = "#03B349";
const GREEN_LIGHT = "#00E05A";
const GREEN_DEEP = "#005D00";

const DepositModal = (props) => {
  const {
    open,
    onClose,
    onBuyPackage,
    onDeposit,
    cryptoCurrency = [],
    loading = false,
    depositConfig,
    chips: chipsProp,
    minDeposit: minDepositProp,
  } = props;

  const chips =
    Array.isArray(chipsProp) && chipsProp.length && chipsProp.every((n) => n > 0)
      ? chipsProp
      : DEFAULT_CHIPS;

  const bonusMultiplier =
    Number(depositConfig?.bonus_multiplier) >= 0 &&
    depositConfig?.bonus_multiplier !== null &&
    depositConfig?.bonus_multiplier !== undefined
      ? Number(depositConfig.bonus_multiplier)
      : DEFAULT_BONUS_MULTIPLIER;

  const popularAmount = chips[1];
  const minDeposit = Number(minDepositProp) > 0 ? Number(minDepositProp) : MIN_DEPOSIT;
  const { t } = useTranslation();
  const theme = useTheme();
  const loginData = useSelector((state) => state.auth.IsLoginData);

  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [amount, setAmount] = useState("");
  const [search, setSearch] = useState("");
  const [prefilled, setPrefilled] = useState(false);

  const currencyCode = loginData?.currency || "USD";
  const currencySymbol = getCurrencySymbol(currencyCode);
  const numericAmount = Number(amount) || 0;
  const bonusAmount = numericAmount * bonusMultiplier;
  const canDeposit = selectedCrypto && numericAmount >= minDeposit && !loading;

  const filteredCurrencies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cryptoCurrency;
    return cryptoCurrency.filter(
      (c) =>
        (c?.name || "").toLowerCase().includes(q) ||
        (c?.symbol || "").toLowerCase().includes(q)
    );
  }, [cryptoCurrency, search]);

  // Reset on every close, including when the parent closes it after Deposit —
  // that path never runs handleClose, so state used to survive to the next open.
  useEffect(() => {
    if (!open) {
      setAmount("");
      setSearch("");
      setSelectedCrypto(null);
      setPrefilled(false);
    }
  }, [open]);

  // Prefill the first chip once, from the API config only — never from
  // DEFAULT_CHIPS, or the fallback 10 would land before the real value arrives
  // and the once-per-open guard would keep it.
  useEffect(() => {
    const firstConfigured = Number(chipsProp?.[0]);
    if (open && !prefilled && firstConfigured > 0) {
      setAmount(String(firstConfigured));
      setPrefilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefilled, chipsProp?.[0]]);

  const handleClose = () => {
    onClose && onClose();
  };

  const submit = (value) => {
    if (!selectedCrypto || !value || value < minDeposit) return;
    onDeposit && onDeposit({ amount: value, currency: selectedCrypto });
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value;
    if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) setAmount(raw);
  };

  const surface = theme.palette.custom.black_white;
  const textMain = theme.palette.custom.white_white;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      BackdropProps={{
        style: {
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0,0,0,0.7)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          backgroundImage: "none",
          backgroundColor: surface,
          border: `1px solid ${GREEN}55`,
          boxShadow: `0 0 60px ${GREEN}33`,
        },
      }}
    >
      {/* ---- Hero bonus banner ---- */}
      <Box
        sx={{
          position: "relative",
          px: { xs: 2.5, sm: 3 },
          py: 1.75,
          background: `linear-gradient(135deg, ${GREEN_DEEP} 0%, ${GREEN} 100%)`,
        }}
      >
        <IconButton
          aria-label={t("depositModal.closeAria", "Close")}
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "#fff",
            backgroundColor: "rgba(0,0,0,0.25)",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.45)" },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={1}>
          <BoltIcon sx={{ color: "#fff", fontSize: 22 }} />
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: { xs: "1.05rem", sm: "1.15rem" },
            }}
          >
            {t("depositModal.eyebrow", "Instant Crypto Deposit")}
          </Typography>
        </Stack>
      </Box>

      <DialogContent
        sx={{
          backgroundColor: surface,
          color: textMain,
          px: { xs: 2.5, sm: 3 },
          pt: 2.5,
          pb: 3,
        }}
      >
        {/* ---- Mode switch ---- */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                borderRadius: 2.5,
                p: 1.5,
                height: "100%",
                border: `1.5px solid ${GREEN}`,
                background: `linear-gradient(135deg, ${GREEN}2E 0%, transparent 100%)`,
                boxShadow: `0 0 18px ${GREEN}30`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <BoltIcon sx={{ fontSize: 18, color: GREEN_LIGHT }} />
                <Typography
                  sx={{ fontWeight: 800, flexGrow: 1, fontSize: "0.9rem", color: GREEN_LIGHT }}
                >
                  {t("depositModal.depositTab", "Quick Deposit")}
                </Typography>
                <CheckCircleIcon sx={{ fontSize: 17, color: GREEN_LIGHT }} />
              </Stack>
              <Typography sx={{ fontSize: "0.72rem", opacity: 0.7, mt: 0.25 }}>
                {t("depositModal.depositHint", "Any amount you want")}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box
              onClick={onBuyPackage || handleClose}
              sx={{
                cursor: "pointer",
                borderRadius: 2.5,
                p: 1.5,
                height: "100%",
                border: `1px solid ${theme.palette.custom.gray_border}44`,
                transition: "all .18s ease",
                "&:hover": { borderColor: `${GREEN}88` },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <RedeemIcon sx={{ fontSize: 18, opacity: 0.65 }} />
                <Typography sx={{ fontWeight: 700, flexGrow: 1, fontSize: "0.9rem" }}>
                  {t("depositModal.packagesTab", "Packages")}
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 18, opacity: 0.5 }} />
              </Stack>
              <Typography sx={{ fontSize: "0.72rem", opacity: 0.6, mt: 0.25 }}>
                {t("depositModal.packagesHint", "Bundles with extra rewards")}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* ---- Crypto picker ---- */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", letterSpacing: 0.4 }}>
            {t("depositModal.chooseCurrency", "PAY WITH")}
          </Typography>
          {selectedCrypto ? (
            <Typography sx={{ fontSize: "0.75rem", color: GREEN_LIGHT, fontWeight: 700 }}>
              {selectedCrypto?.name}
            </Typography>
          ) : null}
        </Stack>

        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("depositModal.searchCurrency", "Search coin")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, opacity: 0.5 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.25,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.04)",
              "& fieldset": { borderColor: `${theme.palette.custom.gray_border}33` },
              "&:hover fieldset": { borderColor: `${GREEN}66` },
              "&.Mui-focused fieldset": { borderColor: GREEN },
            },
          }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(4, 1fr)", sm: "repeat(6, 1fr)" },
            gap: 1,
            maxHeight: 160,
            overflowY: "auto",
            pr: 0.5,
            mb: 2.5,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": { background: `${GREEN}66`, borderRadius: 4 },
          }}
        >
          {filteredCurrencies.map((c) => {
            const active = selectedCrypto?.value === c?.value;
            return (
              <Box
                key={c?.value}
                onClick={() => setSelectedCrypto(c)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 2,
                  height: 56,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0.4,
                  border: `1px solid ${active ? GREEN : `${theme.palette.custom.gray_border}33`}`,
                  background: active
                    ? `linear-gradient(160deg, ${GREEN}3A 0%, transparent 100%)`
                    : "rgba(255,255,255,0.03)",
                  boxShadow: active ? `0 0 14px ${GREEN}55` : "none",
                  transform: active ? "translateY(-2px)" : "none",
                  transition: "all .16s ease",
                  "&:hover": { borderColor: `${GREEN}99`, transform: "translateY(-2px)" },
                }}
              >
                <Avatar src={c?.icon} alt={c?.symbol} sx={{ width: 26, height: 26 }} />
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, lineHeight: 1 }}>
                  {c?.symbol}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* ---- Amount ---- */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", letterSpacing: 0.4 }}>
            {t("depositModal.amount", "AMOUNT")}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", opacity: 0.55 }}>{currencyCode}</Typography>
        </Stack>

        <TextField
          fullWidth
          value={amount}
          onChange={handleAmountChange}
          placeholder="0.00"
          inputMode="decimal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: GREEN_LIGHT }}>
                  {currencySymbol}
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              height: 56,
              fontSize: "1.35rem",
              fontWeight: 800,
              backgroundColor: "rgba(255,255,255,0.04)",
              "& fieldset": { borderColor: `${theme.palette.custom.gray_border}33` },
              "&:hover fieldset": { borderColor: `${GREEN}66` },
              "&.Mui-focused fieldset": { borderColor: GREEN, borderWidth: 2 },
              "&.Mui-focused": { boxShadow: `0 0 22px ${GREEN}33` },
            },
          }}
        />

        <Typography
          sx={{
            mt: 0.75,
            fontSize: "0.72rem",
            color:
              numericAmount > 0 && numericAmount < minDeposit
                ? theme.palette.custom.red
                : "inherit",
            opacity: numericAmount > 0 && numericAmount < minDeposit ? 1 : 0.55,
          }}
        >
          {t("depositModal.minAmount", {
            defaultValue:
              "Minimum deposit is " + formatCurrencyAmount(minDeposit, currencyCode),
            min: minDeposit,
          })}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          {chips.map((value) => (
            <Box
              key={value}
              onClick={() => setAmount(String(value))}
              sx={{
                position: "relative",
                flex: 1,
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.88rem",
                whiteSpace: "nowrap",
                color: "#fff",
                background: `linear-gradient(180deg, ${GREEN_LIGHT} 0%, ${GREEN} 55%, ${GREEN_DEEP} 100%)`,
                boxShadow:
                  numericAmount === value
                    ? `0 0 0 2px #fff, 0 6px 20px ${GREEN}99`
                    : `0 4px 14px ${GREEN}4D`,
                transform: numericAmount === value ? "translateY(-2px)" : "none",
                transition: "all .15s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 22px ${GREEN}77`,
                },
                "&:active": { transform: "translateY(0)" },
              }}
            >
              {formatCurrencyAmount(value, currencyCode, {
                minimumFractionDigits: 0,
                maximumFractionDigits: value % 1 === 0 ? 0 : 2,
              })}
              {value === popularAmount ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: -9,
                    left: "50%",
                    transform: "translateX(-50%)",
                    px: 1,
                    py: "1px",
                    borderRadius: 5,
                    fontSize: "0.55rem",
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    color: "#1a1a1a",
                    backgroundColor: theme.palette.custom.yellow,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("depositModal.popular", "POPULAR")}
                </Box>
              ) : null}
            </Box>
          ))}
        </Stack>

        {/* ---- Bonus preview ---- */}
        <Box
          sx={{
            mt: 2.5,
            borderRadius: 2,
            p: 1.75,
            border: `1px solid ${GREEN}66`,
            background: `linear-gradient(135deg, ${GREEN}26 0%, transparent 85%)`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(180deg, ${GREEN_LIGHT} 0%, ${GREEN_DEEP} 100%)`,
              flexShrink: 0,
            }}
          >
            <BoltIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontSize: "0.72rem", opacity: 0.65, lineHeight: 1.2 }}>
              {t("depositModal.youReceive", "You receive")}
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: "1rem", color: GREEN_LIGHT }}>
              {formatCurrencyAmount(numericAmount, currencyCode)}{" "}
              <Typography component="span" sx={{ fontSize: "0.8rem", opacity: 0.7 }}>
                {t("depositModal.cash", "cash")}
              </Typography>
              {"  +  "}
              {formatCurrencyAmount(bonusAmount, currencyCode)}{" "}
              <Typography component="span" sx={{ fontSize: "0.8rem", opacity: 0.7 }}>
                {t("depositModal.bonus", "bonus")}
              </Typography>
            </Typography>
          </Box>
        </Box>

        <Button
          fullWidth
          disabled={!canDeposit}
          onClick={() => submit(numericAmount)}
          sx={{
            mt: 2,
            height: 50,
            borderRadius: 2,
            fontWeight: 800,
            fontSize: "0.95rem",
            letterSpacing: 0.5,
            textTransform: "none",
            color: "#fff",
            background: `linear-gradient(180deg, ${GREEN_LIGHT} 0%, ${GREEN} 55%, ${GREEN_DEEP} 100%)`,
            boxShadow: `0 6px 24px ${GREEN}66`,
            "&:hover": { boxShadow: `0 10px 32px ${GREEN}88`, filter: "brightness(1.08)" },
            "&.Mui-disabled": {
              background: "rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.35)",
              boxShadow: "none",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            t("depositModal.cta", "Deposit now")
          )}
        </Button>

      </DialogContent>
    </Dialog>
  );
};

export default DepositModal;
